import moment from "moment";
import { PER_PAGE_DEFAULT } from "./Constants";
import { urlEnum } from "./urlEnum";
import { AutocompleteUser } from "../Models/Models";

export type BaseFilterFormState = {
  vin: string | null;
  brand: any | null;
  model: any | null;
  status: any[] | undefined;
  clientName: string | null;
  order: string | undefined;
  page: number;
  perPage: number;
  count: number;
  startDate: string;
  endDate: string;
  user: AutocompleteUser | null | undefined;
};

/** Parse common filter fields from URL search string. */
export function buildBaseFormStateFromUrl(
  search: string,
  cache: any,
  statusCacheKey = "clientMessagesStatus",
): BaseFilterFormState {
  const params = new URLSearchParams(search);

  const brandId = params.get("brand") ? Number(params.get("brand")) : null;
  const modelId = params.get("model") ? Number(params.get("model")) : null;
  const filterStatusParam = params.get("filterStatus");
  const filterStatusIds = filterStatusParam
    ? filterStatusParam.split(",").map(Number)
    : [];

  const brand = brandId
    ? (cache?.brands?.find((b: any) => b.id === brandId) ?? null)
    : null;
  const model = modelId
    ? (cache?.models?.find((m: any) => m.id === modelId) ?? null)
    : null;
  const statusFilter =
    filterStatusIds.length > 0
      ? (cache?.[statusCacheKey]?.filter((s: any) =>
          filterStatusIds.includes(s.id),
        ) ?? undefined)
      : undefined;

  const userId = params.get("user") ? Number(params.get("user")) : null;
  const userName = params.get("userName") || "";
  const user: AutocompleteUser | null = userId
    ? ({ id: userId, name: userName, nume: userName } as AutocompleteUser)
    : null;

  return {
    vin: params.get("vin") || "",
    brand,
    model,
    status: statusFilter?.length ? statusFilter : undefined,
    user,
    clientName: params.get("clientName") || "",
    order: params.get("order") || "desc",
    page: params.get("page") ? Number(params.get("page")) - 1 : 0,
    perPage: params.get("perPage")
      ? Number(params.get("perPage"))
      : PER_PAGE_DEFAULT,
    count: 0,
    startDate:
      params.get("startDate") ||
      moment().subtract(1, "months").format("YYYY-MM-DD"),
    endDate: params.get("endDate") || moment().format("YYYY-MM-DD"),
  };
}

/** Build URLSearchParams from filter state + tab params for browser URL navigation. */
export function buildBaseFilterUrlParams(
  state: BaseFilterFormState,
  tabStatus: string,
  tabIndex: number,
): URLSearchParams {
  const params = new URLSearchParams();
  if (tabStatus) params.set("status", tabStatus);
  params.set("index", String(tabIndex));
  if (state.vin) params.set("vin", state.vin);
  if (state.clientName) params.set("clientName", state.clientName);
  if (state.brand?.id) params.set("brand", String(state.brand.id));
  if (state.model?.id) params.set("model", String(state.model.id));
  if (state.status?.length) {
    params.set(
      "filterStatus",
      state.status.map((s: any) => s.id).join(","),
    );
  }
  if (state.user?.id) {
    params.set("user", String(state.user.id));
    params.set("userName", (state.user as any).name || "");
  }
  params.set("startDate", moment(state.startDate).format("YYYY-MM-DD"));
  params.set("endDate", moment(state.endDate).format("YYYY-MM-DD"));
  params.set("order", state.order ?? "desc");
  params.set("page", String(state.page + 1));
  params.set("perPage", String(state.perPage));
  return params;
}

/**
 * Build the backend API URL from browser URL params.
 * @param urlParams        Parsed URLSearchParams from location.search
 * @param messageType      e.g. "contactMessage" | "financingRequest" | "VINRequest"
 * @param userId           current user id from localStorage
 * @param defaultStatus    fallback tab status when none in URL
 * @param tabStatusOverride pass "null" for VINRequests (backend ignores tab status)
 * @param extraQueryString  optional extra params appended at end (e.g. "&followUpFilter=0&sortField=created_at")
 */
export function buildApiUrl(
  urlParams: URLSearchParams,
  messageType: string,
  userId: string | null,
  defaultStatus: string,
  tabStatusOverride?: string,
  extraQueryString = "",
): string {
  const statusCode =
    tabStatusOverride !== undefined
      ? tabStatusOverride
      : urlParams.get("status") || defaultStatus;
  const page = urlParams.get("page") ? Number(urlParams.get("page")) - 1 : 0;
  const perPage = urlParams.get("perPage")
    ? Number(urlParams.get("perPage"))
    : PER_PAGE_DEFAULT;
  const startDate =
    urlParams.get("startDate") ||
    moment().subtract(1, "months").format("YYYY-MM-DD");
  const endDate = urlParams.get("endDate") || moment().format("YYYY-MM-DD");

  return (
    `${urlEnum.clientMessages}?type=${messageType}&page=${page}&perPage=${perPage}` +
    `&vin=${urlParams.get("vin") || ""}` +
    `&brand_id=${urlParams.get("brand") || ""}` +
    `&model_id=${urlParams.get("model") || ""}` +
    `&clientName=${urlParams.get("clientName") || ""}` +
    `&order=${urlParams.get("order") || "desc"}` +
    `&startDate=${startDate}&endDate=${endDate}` +
    `&firstSeenUser=${userId}&status=${statusCode}` +
    `&filterStatus=${urlParams.get("filterStatus") || ""}` +
    `&filterUser=${urlParams.get("user") || ""}` +
    extraQueryString
  );
}
